import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { useCurrency } from "../../lib/currency";
import { Menu } from "lucide-react";
import { NavSwitchers } from "./navbar/NavSwitchers";
import { UserMenu } from "./navbar/UserMenu";
import { MobileMenu } from "./navbar/MobileMenu";

export default function Navbar({ variant = "light" }) {
    const { user, logout } = useAuth();
    const { t, lang, setLang } = useT();
    const { code: curCode, setCode: setCurCode, all: currencies } = useCurrency();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { to: "/cabins", label: t("nav.cabins"), key: "cabins" },
        { to: "/experiences", label: t("nav.experiences"), key: "experiences" },
        { to: "/story", label: t("nav.story"), key: "story" },
        { to: "/gallery", label: t("nav.gallery"), key: "gallery" },
        { to: "/contact", label: t("nav.contact"), key: "contact" },
    ];

    const isDark = variant === "transparent" && !scrolled;
    const textColor = isDark ? "text-bone" : "text-ink";
    const linkHover = isDark ? "hover:text-bone/70" : "hover:text-forest";

    return (
        <header
            data-testid="site-navbar"
            className={`mobile-solid-nav fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${scrolled ? "glass-nav" : (variant === "transparent" ? "bg-transparent" : "glass-nav")}`}
        >
            <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
                <Link to="/" data-testid="brand-link" className="flex items-center">
                    <img src="/brand-logo.png?v=4" alt="Wild Coffee Retreat" className="h-16 w-auto -my-2" />
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.key}
                            to={l.to}
                            data-testid={`nav-${l.key}`}
                            className={({ isActive }) =>
                                `font-sans text-sm tracking-wide ${textColor} ${linkHover} transition-colors ${isActive ? "underline underline-offset-8 decoration-1" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <NavSwitchers
                        textColor={textColor} linkHover={linkHover}
                        curCode={curCode} setCurCode={setCurCode} currencies={currencies}
                        lang={lang} setLang={setLang} t={t}
                    />
                    <UserMenu user={user} logout={logout} navigate={navigate} t={t} textColor={textColor} linkHover={linkHover} />
                    <Link
                        to="/book"
                        data-testid="nav-reserve-btn"
                        className="font-sans text-xs uppercase tracking-widest2 px-6 py-3 bg-forest text-bone hover:bg-forestDark transition-colors"
                    >
                        {t("nav.reserve")}
                    </Link>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(true)}
                    className="lg:hidden text-ink"
                    aria-label="Open menu"
                >
                    <Menu size={24} />
                </button>
            </div>

            <MobileMenu
                open={open} setOpen={setOpen} links={links}
                user={user} logout={logout} navigate={navigate}
                lang={lang} setLang={setLang} t={t}
            />
        </header>
    );
}
